import { graphql } from 'react-relay';
import React, { memo, useContext, useState, useCallback, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import type { MutationUpdaterFn } from 'apollo-client';
import { css } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types/emitter';
import { AnnotationMarkStates, AnnotationTypes } from '@atlaskit/adf-schema/annotation';
import type { AddMarkStep } from '@atlaskit/editor-prosemirror/transform';
import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';
import { isCanvasContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter.ts';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter.ts';
import {
	reopenCommentInDataMap,
	resolveCommentInDataMap,
	updateCommentsQueryCacheForResolve,
} from '@confluence/comments-data/entry-points/commentsDataHelper.ts';
import {
	useCommentsData,
	useCommentsDataActions,
	CommentActionType,
	CommentType,
	type CommentData,
	type ReplyData as LegacyReplyData,
	type CommentsDataMap as LegacyCommentsDataMap,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { useAllCommentsData } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { useUpdateCommentInCache } from '@confluence/comments-data-manager/entry-points/useUpdateCommentInCache.ts';
import {
	resolveCommentInCacheCallback,
	reopenCommentInCacheCallback,
	selectUpdateFunction,
	type CommentsDataMap as NewCommentsDataMap,
	type ReplyData as NewReplyData,
} from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { useProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { useCommentsContentActions } from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { useUpdateDocument } from '@confluence/comment-context/entry-points/useUpdateDocument.ts';
import { ReactionsContext } from '@confluence/comment-context/entry-points/ReactionsContext.ts';
import { InlineCommentFramework } from '@confluence/inline-comments-common/entry-points/InlineCommentFramework.ts';
import { EditComment } from '@confluence/inline-comments-common/entry-points/EditComment.ts';
import { CommentBody } from '@confluence/inline-comments-common/entry-points/CommentBody.ts';
import {
	defaultReactionsSummary,
	handleResolveSuccess,
	handleDeleteSuccess,
	handleMutationFailure,
	insertReopenedCommentAnnotation,
	updateApolloCacheCallback as updateApolloInlineReplyCacheCallback,
	getTranslatedError,
	isAlreadyDeletedError,
	parseError,
	resolveInlineCommentAnnotation,
	unresolveInlineCommentAnnotation,
} from '@confluence/inline-comments-common/entry-points/inlineCommentsUtils.ts';
import type {
	CommentAction,
	MutationResult,
} from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes.ts';
import { usePageContentId, useContentType } from '@confluence/page-context';
import { CommentDeletionLocation } from '@confluence/inline-comments-queries/entry-points/DeleteInlineCommentMutation.ts';
import { InlineCommentsQuery } from '@confluence/inline-comments-queries/entry-points/InlineCommentsQuery.graphql.ts';
import { buildConsistentActiveCommentsQueryVariables } from '@confluence/comments-panel-queries/entry-points/buildConsistentQueryVariablesHelper.ts';
import type { CommentFooterLocation } from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery.ts';
import { ActiveCommentsQuery } from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery.graphql.ts';
import { useInlineCommentQueryParams } from '@confluence/comment/entry-points/useCommentQueryParams.ts';
import type { InlineCommentAuthorKnownUser } from '@confluence/inline-comments-queries/entry-points/InlineCommentsQuery.ts';
import type {
	TopLevelComment,
	CommentLocation,
	CommentInlineResolveProperties,
	GeneralCommentLocation,
} from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';
import type { GraphQLContentStatus } from '@confluence/inline-comments-queries/entry-points/InlineCommentQuery.ts';
import { useEditorAnnotations } from '@confluence/inline-comments-hooks/entry-points/useEditorAnnotations.ts';
import { useInlineCommentsActions } from '@confluence/inline-comments-hooks/entry-points/useInlineComments.ts';
import {
	RESOLVE_INLINE_COMMENT_EXPERIENCE,
	RESOLVE_PAGE_COMMENT_EXPERIENCE,
	DELETE_INLINE_COMMENT_EXPERIENCE,
	DELETE_PAGE_COMMENT_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { getCommentsDataManagerExperimentAttributes } from '@confluence/experience-tracker/entry-points/getCommentsDataManagerExperimentAttributes.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import type { PageInfoNode } from '@confluence/page-info/entry-points/PageInfoQuery.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useRendererActions } from '@confluence/renderer-actions/entry-points/RendererActionsContext.ts';
import {
	useAnnotationsIsOffline,
	useAnnotationsDispatch,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { commentsi18n } from '@confluence/comments-util/entry-points/commentsi18n.ts';
import { DEFAULT_LIMIT } from '@confluence/page-comments-queries';
import { i18n as commentsI18n } from '@confluence/inline-comments-common/entry-points/i18n.ts';
import {
	CommentsSectionQuery,
	CommentsSectionWithoutReactionsQuery,
} from '@confluence/page-comments-queries/entry-points/CommentsSectionQuery.graphql.ts';
import type {
	CommentsSectionQuery as CommentsSectionQueryType,
	CommentsSectionWithoutReactionsQuery as CommentsSectionWithoutReactionsQueryType,
	CommentsSectionQueryVariables,
} from '@confluence/page-comments-queries/entry-points/__types__/CommentsSectionQuery.ts';
import {
	updateApolloCacheParentCallback as updateApolloCacheGeneralParentCallback,
	updateApolloCacheReplyCallback as updateApolloCacheGeneralReplyCallback,
	type UpdateApolloCacheParentCallbackDataType as UpdateApolloCacheGeneralParentCallbackDataType,
	type UpdateApolloCacheReplyCallbackDataType as UpdateApolloCacheGeneralReplyCallbackDataType,
} from '@confluence/page-comments-queries/entry-points/pageCommentUtils.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getLogger } from '@confluence/logger';
import { useIsNewDanglingCommentExperienceEnabled } from '@confluence/comments-panel-utils/entry-points/useIsNewDanglingCommentExperienceEnabled.ts';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics.ts';
import {
	handleCommentsPanelNavigationWithViewChange,
	ScrollBehavior,
	NavigationContext,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import { buildMutationContextForNoNetworkError } from '@confluence/comments-util/entry-points/errorUtils.ts';
import { constructStepForGql } from '@confluence/comments-util/entry-points/rendererAnnotations.ts';
import {
	ViewValues,
	useCommentsPanel,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { useCommentService } from '@confluence/content-types-bridge-comments';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';

import { useShowCommentsPanel } from '../useShowCommentsPanel.ts';
import {
	deleteInlineParentComment,
	deleteParentCommentOptimistically,
	deleteReplyCommentOptimistically,
} from '../helper/commentHelpers.ts';
import { arePropsShallowEqualWhenMemoRowsEnabled } from '../helper/memoRowsExperiment.ts';

import type { CommentResolveCommentMutation_Mutation as CommentResolveCommentMutation_Mutation$data } from './__types__/CommentResolveCommentMutation.ts';
import type { CommentReopenCommentMutation as CommentReopenCommentMutation_Mutation$data } from './__types__/CommentReopenCommentMutation.ts';
import type {
	CommentDeleteInlineCommentMutationWithStepMutation$data,
	CommentDeleteInlineCommentMutationWithStepMutation$variables,
} from './__generated__/CommentDeleteInlineCommentMutationWithStepMutation.graphql.ts';
import type {
	CommentDeleteInlineCommentMutation$data,
	CommentDeleteInlineCommentMutation$variables,
} from './__generated__/CommentDeleteInlineCommentMutation.graphql.ts';
import type {
	CommentDeleteGeneralCommentMutation$data,
	CommentDeleteGeneralCommentMutation$variables,
} from './__generated__/CommentDeleteGeneralCommentMutation.graphql.ts';

const CommentResolveCommentMutation = graphql`
	mutation CommentResolveCommentMutation($commentIds: [ID]!, $cloudId: ID!) {
		confluence_resolveComments(commentIds: $commentIds, cloudId: $cloudId) {
			success
			commentResolutionStates {
				commentId
				resolveProperties {
					resolvedTime
					resolvedUser
					resolvedFriendlyDate
				}
				status
			}
			errors {
				message
				extensions {
					... on ResolveCommentsMutationErrorExtension {
						errorType
						statusCode
					}
					__typename
				}
			}
		}
	}
`;
const CommentReopenCommentMutation = graphql`
	mutation CommentReopenCommentMutation($commentId: ID!, $cloudId: ID!) {
		confluence_reopenComment(commentId: $commentId, cloudId: $cloudId) {
			success
			commentResolutionStates {
				commentId
				status
			}
			errors {
				message
				extensions {
					... on ReopenCommentsMutationErrorExtension {
						errorType
						statusCode
					}
					__typename
				}
			}
		}
	}
`;
const CommentDeleteInlineCommentMutationWithStepMutation = graphql`
	mutation CommentDeleteInlineCommentMutationWithStepMutation($input: DeleteInlineCommentInput!) {
		deleteInlineComment(input: $input)
	}
`;
const CommentDeleteInlineCommentMutation = graphql`
	mutation CommentDeleteInlineCommentMutation(
		$commentIdToDelete: ID!
		$deleteFrom: CommentDeletionLocation
	) {
		deleteComment(commentIdToDelete: $commentIdToDelete, deleteFrom: $deleteFrom)
	}
`;
const CommentDeleteGeneralCommentMutation = graphql`
	mutation CommentDeleteGeneralCommentMutation(
		$commentIdToDelete: ID!
		$deleteFrom: CommentDeletionLocation
	) {
		deleteComment(commentIdToDelete: $commentIdToDelete, deleteFrom: $deleteFrom)
	}
`;
type CommentProps = {
	comment: CommentData | LegacyReplyData;
	supportedTopLevelActions: CommentAction[];
	pageMode: PageMode;
	pageInfo: PageInfoNode | null;
	isUnread?: boolean;
	isHovered?: boolean;
	threadKey: string;
	flags?: FlagsStateContainer;
	isAnnotatedTextPressed?: boolean;
	setAnnotatedTextPressed?: (state: boolean) => void;
	adjustForNestedReplies?: boolean;
	hideBranchingStyle?: boolean;
	canAddComments?: boolean;
	isThreadHovered?: boolean;
	isParentCommentOpen?: boolean;
	isStandaloneComment?: boolean;
	showRepliesWithCurvedBorders?: boolean;
	deleteStandaloneComment?: (parentCommentId: string, commentId: string) => void;
	replyToComment?: (commentId: string, commentData: LegacyReplyData) => void;
	updateReactionsCacheFn?: (
		emojiId: string,
		actionType: 'add' | 'delete',
		contentId: string,
		containerId?: string,
	) => MutationUpdaterFn<any> | void;
	onRendered?: () => void;
	isLastUnnestableReply?: boolean;
	commentHandlesPadding?: boolean;
	hasParentMaxHeight?: boolean;
	hideReactions?: boolean;
};

const i18n = defineMessages({
	commentReopenedFlagTitle: {
		id: 'comments-panel.comment.reopened.flag.title',
		defaultMessage: 'Comment reopened',
		description:
			'Title to display in Flag to inform user when the comment was successfully reopened.',
	},
	commentReopenFailedFlagTitle: {
		id: 'comments-panel.comment.reopen.failed.flag.title',
		defaultMessage: 'Failed to reopen comment',
		description: 'Title to display in Flag to inform user when the comment failed to reopen.',
	},
	commentReopenCacheUpdateFailedFlagTitle: {
		id: 'comments-panel.comment.reopen.cache-update.failed.flag.title',
		defaultMessage: 'Failed to update reopened comment. Please refresh the page.',
		description:
			'Title to display in Flag to inform user when the comment was reopened but the local cache update failed.',
	},
	retryCTA: {
		id: 'comments-panel.comment.reopen.failed.flag.cta.retry',
		defaultMessage: 'Retry now',
		description:
			'CTA button text displayed in Error Flag for users to retry the failed comment reopen action.',
	},
});

const logger = getLogger('page-comments-queries');

const commentWrapperReplyPaddingOverrides = css({
	// paddingTop: token('space.100'),
	paddingLeft: token('space.300'),
	paddingBottom: token('space.100'),
});

const commentWrapperReplyPaddingOverridesV2 = css({
	paddingLeft: token('space.200'),
});

const standaloneCommentWrapperReplyPaddingOverrides = css({
	marginLeft: token('space.150'),
});

const leftBorderStyle = css({
	borderLeft: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
});

const leftBorderStyleUpdated = css({
	borderLeft: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
});

// this component can be used for parent comment and a reply
const CommentComponent = ({
	comment,
	supportedTopLevelActions,
	pageMode,
	pageInfo,
	isUnread = false,
	isHovered,
	threadKey,
	flags,
	isAnnotatedTextPressed,
	setAnnotatedTextPressed,
	adjustForNestedReplies = false,
	hideBranchingStyle = false,
	hideReactions = false,
	canAddComments = true,
	isThreadHovered,
	isParentCommentOpen,
	isStandaloneComment,
	showRepliesWithCurvedBorders,
	deleteStandaloneComment,
	replyToComment,
	updateReactionsCacheFn,
	onRendered,
	isLastUnnestableReply = false,
	commentHandlesPadding = false,
	hasParentMaxHeight = false,
}: CommentProps): React.JSX.Element => {
	// Store stable references to break circular dependency in useCallback
	const handleResolveCommentRef = useRef<(() => void) | null>(null);
	const handleReopenCommentRef = useRef<(() => void) | null>(null);
	const [editCommentId, setCommentForEdit] = useState('');
	const { isReactionsEnabled } = useContext(ReactionsContext);
	const { commentService } = useCommentService();
	const isNewDanglingCommentExperienceEnable = useIsNewDanglingCommentExperienceEnabled();

	const isOffline = useAnnotationsIsOffline();
	const { setEditorViewProps } = useAnnotationsDispatch();

	const experienceTracker = useContext(ExperienceTrackerContext);
	const { resetContentChanged } = useCommentsContentActions();
	const [contentId = ''] = usePageContentId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { removeUnresolvedInlineComment, addUnresolvedInlineComment } = useInlineCommentsActions();
	const [
		{ commentsDataMap: legacyCommentsDataMap, orderedActiveAnnotationIdList, cacheUpdateFailed },
		{
			addNewCommentThreads,
			addReplyToCommentThread,
			deleteParentComment,
			handleRemovingComments,
			setOrderedActiveAnnotationIdList,
		},
	] = useCommentsData();
	const { setCacheUpdateFailed } = useCommentsDataActions();

	// Get cache operations for new comments data manager
	const { deleteCommentInCache } = useAllCommentsData({
		contentId,
		options: {
			skip: true, // Only using write operations, no need to fetch data
		},
	});

	// Get processed comments data for new comments data manager
	const processedCommentsData = useProcessedCommentsDataState();

	// Use new or legacy data based on experiment
	const commentsDataMap = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? processedCommentsData.commentsDataMap
		: legacyCommentsDataMap;
	const [{ annotations }] = useEditorAnnotations();
	const { rendererActions } = useRendererActions();
	const [{ updateDocumentFn }] = useUpdateDocument();
	const { cloudId } = useSessionData();

	const [
		{ currentlySelectedThreadKey, currentView },
		{ setCurrentView, setCurrentlySelectedThreadKey, setReplyToCommentId },
	] = useCommentsPanel();
	const { formatMessage } = useIntl();
	const { focusedCommentId } = useInlineCommentQueryParams();
	const { isCommentsPanelShown, showCommentsPanel } = useShowCommentsPanel();
	const [contentType] = useContentType();

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const [resolveCommentFn] = useMutationInterop<CommentResolveCommentMutation_Mutation$data>(
		CommentResolveCommentMutation,
		buildMutationContextForNoNetworkError(),
	);

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const [reopenCommentFn] = useMutationInterop<CommentReopenCommentMutation_Mutation$data>(
		CommentReopenCommentMutation,
		{
			refetchQueries:
				// Copied from the existing dialog flow to support standalone experience
				pageMode === PageMode.VIEW &&
				!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
					? [{ query: InlineCommentsQuery, variables: { pageId: contentId } }]
					: [],
			...buildMutationContextForNoNetworkError(),
		},
	);

	const [deleteTopLevelRendererInlineCommentFn] = useMutationInterop<
		CommentDeleteInlineCommentMutationWithStepMutation$data,
		CommentDeleteInlineCommentMutationWithStepMutation$variables
	>(CommentDeleteInlineCommentMutationWithStepMutation, buildMutationContextForNoNetworkError());

	const [deleteInlineCommentFn] = useMutationInterop<
		CommentDeleteInlineCommentMutation$data,
		CommentDeleteInlineCommentMutation$variables
	>(CommentDeleteInlineCommentMutation, buildMutationContextForNoNetworkError());

	const [deleteGeneralCommentFn] = useMutationInterop<
		CommentDeleteGeneralCommentMutation$data,
		CommentDeleteGeneralCommentMutation$variables
	>(CommentDeleteGeneralCommentMutation, buildMutationContextForNoNetworkError());
	// Get the update functions for the comments data manager - aliased to support the different
	// mutation's different result types
	const { updateCommentInCache: updateResolveCommentInCache } =
		useUpdateCommentInCache<CommentResolveCommentMutation_Mutation$data>();
	const { updateCommentInCache: updateReopenCommentInCache } =
		useUpdateCommentInCache<CommentReopenCommentMutation_Mutation$data>();

	const eventEmitter =
		pageMode === PageMode.VIEW
			? getRendererAnnotationEventEmitter()
			: getEditorAnnotationEventEmitter();

	const userId = (comment?.author as InlineCommentAuthorKnownUser)?.accountId;
	const topCommentUserAvatar = comment?.author?.profilePicture?.path;
	const topCommentDisplayName =
		comment?.author?.displayName ?? formatMessage(commentsi18n.anonymous);
	const commentId = comment?.id;
	const date = comment?.version?.when;
	const createdAt = comment?.createdAt?.value;
	const createdAtNonLocalized = comment?.createdAtNonLocalized;
	const versionByDisplayName = comment?.version?.by?.displayName;
	const versionDate = comment?.version?.when;
	const permissions = comment?.permissions;
	const content = comment?.body?.value;
	const permissionType = comment?.author?.permissionType ?? undefined;
	const parentCommentId = comment?.parentId;
	const isReply = !!parentCommentId;
	const isEdited = (comment?.version?.number || 0) > 1;
	const numReplies = (comment as TopLevelComment)?.replies?.length || 0;
	const commentUrl = pageMode === PageMode.EDIT ? comment?.links?.editui : comment?.links?.webui; // view page and live docs uses the webui field
	const isDatabaseOrWhiteboard = contentType === 'database' || isCanvasContentType(contentType);

	// When hideReactions (e.g. comment preview popup), pass empty summary so reactions query is skipped and none render
	const inheritedReactionsData = hideReactions
		? defaultReactionsSummary
		: (comment?.reactionsSummary ?? null);

	const inlineCommentlocation = comment?.location as CommentLocation;
	const footerCommentLocation = comment?.location as CommentFooterLocation;

	const commentType =
		inlineCommentlocation?.type === 'INLINE' ? CommentType.INLINE : CommentType.GENERAL;

	const getResolvedProperties = useCallback(
		(type: CommentType) => {
			const properties =
				type === CommentType.INLINE
					? inlineCommentlocation?.inlineResolveProperties
					: footerCommentLocation?.commentResolveProperties;
			if (!isReply) {
				return properties || undefined;
			}
			return undefined;
		},
		[inlineCommentlocation, footerCommentLocation, isReply],
	);

	const resolveProperties = getResolvedProperties(commentType);

	const annotatedText = inlineCommentlocation?.inlineText || undefined;

	const isCommentRemovedByAnotherUser = comment?.wasRemovedByAnotherUser;

	const isResolvedByAnotherUser =
		isCommentRemovedByAnotherUser &&
		isCommentRemovedByAnotherUser === CommentActionType.RESOLVE_COMMENT_THREAD;
	const isDeletedByAnotherUser =
		isCommentRemovedByAnotherUser &&
		isCommentRemovedByAnotherUser === CommentActionType.DELETE_COMMENT;

	const deleteCommentInCacheOnUpdate = useCallback(
		(removalCommentId: string, experienceName: string) =>
			deleteCommentInCache({
				commentId: removalCommentId,
				onError: (err) => {
					void handleMutationFailure({
						experienceTracker,
						experienceName,
						error: err,
						commentId: removalCommentId,
					});
				},
			}),
		[deleteCommentInCache, experienceTracker],
	);

	const isOptimisticDeleteEnabled =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		fg('confluence_frontend_content_wrapper') &&
		expVal('confluence_fe_delete_comment_optimistic', 'isEnabled', false);

	const handleDeleteInlineComment = () => {
		// NOTE: The DELETE experience is started in CommentActions.tsx
		const isDangling = (resolveProperties as CommentInlineResolveProperties)?.isDangling;
		const isTopLevelRendererComment =
			!isDatabaseOrWhiteboard && pageMode === PageMode.VIEW && !isReply;

		const canDeleteAnnotation = !isDangling && isTopLevelRendererComment;

		let step: AddMarkStep | undefined;

		if (canDeleteAnnotation) {
			try {
				const deleteResult = rendererActions?.deleteAnnotation(threadKey, 'inlineComment');
				if (deleteResult) {
					step = deleteResult.step as AddMarkStep;
				} else {
					experienceTracker.stopOnError({
						name: DELETE_INLINE_COMMENT_EXPERIENCE,
						error: new Error(`Unable to remove annotation ${threadKey} from document`),
					});
					return;
				}
			} catch (e) {
				experienceTracker.stopOnError({
					name: DELETE_INLINE_COMMENT_EXPERIENCE,
					error: e,
				});
				return;
			}
		}
		if (isStandaloneComment) {
			deleteStandaloneComment &&
				parentCommentId &&
				deleteStandaloneComment(parentCommentId, commentId);
			return;
		}

		if (fg('confluence_frontend_content_wrapper')) {
			resetContentChanged();
		}

		// Optimistically mark the annotation as resolved.
		// If deletion is successful, the annotation will be deleted later;
		// if deletion fails, the annotation will be unresolved.
		if (!isReply && isOptimisticDeleteEnabled) {
			resolveInlineCommentAnnotation(threadKey, pageMode, eventEmitter);
		}
		// Optimistically delete the comment thread, if it is not a reply.
		let deletedParentComment = null;
		if (!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
			if (!isReply && isOptimisticDeleteEnabled) {
				deletedParentComment = deleteParentCommentOptimistically({
					threadKey,
					commentType,
					commentsDataMap,
					deleteParentComment,
				});
			} else {
				deletedParentComment = deleteInlineParentComment({
					isReply,
					threadKey,
					commentType,
					commentsDataMap,
					deleteParentComment,
				});
			}
		}
		// Optimistically delete the reply, if it is a reply.
		const deletedReplyComment =
			!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) &&
			isReply &&
			isOptimisticDeleteEnabled
				? deleteReplyCommentOptimistically({
						threadKey,
						commentId,
						commentType,
						commentsDataMap,
						handleRemovingComments,
					})
				: null;

		const deleteMutation = canDeleteAnnotation
			? deleteTopLevelRendererInlineCommentFn
			: deleteInlineCommentFn;
		const optimisticDeleteResponse = canDeleteAnnotation
			? { deleteInlineComment: true }
			: { deleteComment: true };
		const deleteVariables = {
			variables: canDeleteAnnotation
				? {
						input: {
							commentId,
							step: step ? constructStepForGql(step) : {},
						},
					}
				: {
						commentIdToDelete: commentId,
						// There is no VIEW option
						deleteFrom:
							pageMode === PageMode.EDIT
								? CommentDeletionLocation.EDITOR
								: CommentDeletionLocation.LIVE,
					},
			update: selectUpdateFunction<MutationResult>(
				expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
				() => deleteCommentInCacheOnUpdate(commentId, DELETE_INLINE_COMMENT_EXPERIENCE),
				() =>
					isReply
						? updateApolloInlineReplyCacheCallback(
								'delete',
								{
									pageId: contentId,
									contentStatus: ['CURRENT', 'DRAFT'] as GraphQLContentStatus[],
								},
								parentCommentId,
								commentId,
							)
						: undefined,
			),
			optimisticResponse: isOptimisticDeleteEnabled ? optimisticDeleteResponse : undefined,
		};

		deleteMutation({
			...deleteVariables,
		} as any)
			.then(({ data }: { data?: any }) => {
				if (
					!data ||
					(canDeleteAnnotation && !data.deleteInlineComment) ||
					(!canDeleteAnnotation && !data.deleteComment)
				) {
					throw new Error('No data returned from mutation');
				}

				const cleanUpReply = () => {
					if (!isOptimisticDeleteEnabled) {
						if (!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
							handleRemovingComments({
								threadKey,
								commentId,
								action: CommentActionType.DELETE_COMMENT,
								commentType,
							});
						}
					}
				};

				const deleteAnnotationAndCleanUpParent = (annotationId: string) => {
					// For a top level comment in renderer, we need to send out the events to tell the
					// renderer to deselect the annotation and remove it from the document
					if (pageMode === PageMode.VIEW) {
						const deleteResult = rendererActions?.deleteAnnotation(annotationId, 'inlineComment');

						if (deleteResult && deleteResult.doc) {
							// Inform the Renderer to de-select the annotation
							eventEmitter.emit(AnnotationUpdateEvent.REMOVE_ANNOTATION_FOCUS);
							eventEmitter.emit(AnnotationUpdateEvent.DESELECT_ANNOTATIONS);
							updateDocumentFn && updateDocumentFn(deleteResult.doc);
						}

						// For editor/live pages we need to send the event to delete the mark
					} else {
						eventEmitter.emit('delete', annotationId);
					}

					if (!fg('confluence_frontend_content_wrapper')) {
						resetContentChanged();
						if (!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
							deleteParentComment({
								threadKey,
								commentType,
							});
						}

						if (commentType === CommentType.INLINE) {
							setOrderedActiveAnnotationIdList(
								orderedActiveAnnotationIdList
									.map((status) => status.threadKey)
									.filter((id) => id !== threadKey),
							);
						}
					}
				};

				// send bridge api signal to content types.
				commentService.onDeleteComment?.(threadKey);

				const onSuccess = (annotationId: string) => {
					if (isReply) {
						cleanUpReply();
					} else {
						deleteAnnotationAndCleanUpParent(annotationId);
					}

					experienceTracker.succeed({
						name: DELETE_INLINE_COMMENT_EXPERIENCE,
					});
				};

				handleDeleteSuccess({
					commentId,
					annotationId: threadKey,
					pageId: contentId,
					pageMode,
					isReply,
					source: isStandaloneComment ? AnalyticsSource.STANDALONE : AnalyticsSource.COMMENTS_PANEL,
					inlineNodeTypes: undefined, // We need to figure out how to get this data
					createAnalyticsEvent,
					removeUnresolvedInlineComment,
					onSuccess,
				});
			})
			.catch((err) => {
				handleDeleteCommentError({
					err,
					deletedParentComment,
					deletedReplyComment,
				});
			});
	};

	const handleDeleteGeneralComment = () => {
		// Optimistically delete the comment thread, if it is not a reply.
		const deletedParentComment =
			!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) &&
			!isReply &&
			isOptimisticDeleteEnabled
				? deleteParentCommentOptimistically({
						threadKey,
						commentType,
						commentsDataMap,
						deleteParentComment,
					})
				: null;
		// Optimistically delete the reply, if it is a reply.
		const deletedReplyComment =
			!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) &&
			isReply &&
			isOptimisticDeleteEnabled
				? deleteReplyCommentOptimistically({
						threadKey,
						commentId,
						commentType,
						commentsDataMap,
						handleRemovingComments,
					})
				: null;

		let deleteLocation: CommentDeletionLocation | undefined;

		// There is no deleteLocation for View Page
		if (pageMode !== PageMode.VIEW) {
			deleteLocation =
				pageMode === PageMode.EDIT ? CommentDeletionLocation.EDITOR : CommentDeletionLocation.LIVE;
		}

		const queryVariables = buildConsistentActiveCommentsQueryVariables({
			pageId: contentId,
			type: 'FOOTER', // If we have footer, always write to footer. Panel works either way
		});

		const deleteVariables = {
			variables: {
				commentIdToDelete: commentId,
				deleteFrom: deleteLocation,
			},
			update: selectUpdateFunction<CommentDeleteGeneralCommentMutation$data>(
				expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
				() => deleteCommentInCacheOnUpdate(commentId, DELETE_PAGE_COMMENT_EXPERIENCE),
				() =>
					isReply
						? (updateApolloCacheGeneralReplyCallback(
								'delete',
								queryVariables,
								parentCommentId,
								isReactionsEnabled,
								commentId,
							) as MutationUpdaterFn<UpdateApolloCacheGeneralReplyCallbackDataType>)
						: (updateApolloCacheGeneralParentCallback(
								'delete',
								queryVariables,
								isReactionsEnabled,
								commentId,
							) as MutationUpdaterFn<UpdateApolloCacheGeneralParentCallbackDataType>),
			),
			optimisticResponse: isOptimisticDeleteEnabled ? { deleteComment: true } : undefined,
		};

		deleteGeneralCommentFn({ ...deleteVariables })
			.then(({ data }) => {
				if (!data || !data.deleteComment) {
					throw new Error('No data returned from mutation');
				}

				resetContentChanged();

				if (!isOptimisticDeleteEnabled) {
					if (isReply) {
						if (!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
							handleRemovingComments({
								threadKey,
								commentId,
								action: CommentActionType.DELETE_COMMENT,
								commentType,
							});
						}
					} else {
						if (!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
							deleteParentComment({
								threadKey,
								commentType,
							});
						}
					}
				}

				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'deleted',
						actionSubject: 'comment',
						actionSubjectId: commentId,
						objectType: 'page',
						objectId: contentId,
						source: AnalyticsSource.COMMENTS_PANEL,
						attributes: {
							...getCommentsDataManagerExperimentAttributes(),
							isReply,
							commentType: CommentType.GENERAL,
							source: isStandaloneComment
								? AnalyticsSource.STANDALONE
								: AnalyticsSource.COMMENTS_PANEL,
							pageMode,
						},
					},
				}).fire();

				experienceTracker.succeed({
					name: DELETE_PAGE_COMMENT_EXPERIENCE,
				});
			})
			.catch((err) => {
				handleDeleteCommentError({
					err,
					deletedParentComment,
					deletedReplyComment,
				});
			});
	};

	const handleDeleteCommentError = ({
		err,
		deletedParentComment,
		deletedReplyComment,
	}: {
		err: Error;
		deletedParentComment: LegacyCommentsDataMap | NewCommentsDataMap | null;
		deletedReplyComment: LegacyReplyData | NewReplyData | null;
	}) => {
		const isInlineComment = commentType === CommentType.INLINE;

		const parsedError = parseError(err);
		const translatedError = getTranslatedError(parsedError.message, commentId);

		if (isAlreadyDeletedError(translatedError)) {
			void flags?.showWarningFlag({
				id: commentId,
				title: <FormattedMessage {...commentsI18n.commentAlreadyDeletedTitle} />,
				description: <FormattedMessage {...translatedError} />,
			});

			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'alreadyDeleted',
					actionSubject: 'comment',
					actionSubjectId: commentId,
					objectType: 'page',
					objectId: contentId,
					source: AnalyticsSource.COMMENTS_PANEL,
					attributes: {
						...getCommentsDataManagerExperimentAttributes(),
						commentType,
						mode: pageMode,
						framework: isInlineComment ? InlineCommentFramework.ANNOTATION_PROVIDER : undefined,
						isReply,
					},
				},
			}).fire();
		} else if (isOptimisticDeleteEnabled) {
			// show an error flag to the user when optimistic deletion fails
			void flags?.showErrorFlag({
				id: commentId,
				title: <FormattedMessage {...commentsI18n.deleteErrorTitle} />,
				description: <FormattedMessage {...commentsI18n.deleteError} />,
			});
		}

		if (deletedParentComment && fg('confluence_frontend_content_wrapper')) {
			if (!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
				addNewCommentThreads(deletedParentComment as LegacyCommentsDataMap);
			}
		}
		if (isInlineComment && !isReply && isOptimisticDeleteEnabled) {
			unresolveInlineCommentAnnotation(threadKey, pageMode, eventEmitter);
		}

		if (deletedReplyComment && isOptimisticDeleteEnabled) {
			if (!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
				addReplyToCommentThread(
					threadKey,
					deletedReplyComment as LegacyReplyData,
					commentType,
					true,
				);
			}
		}

		void handleMutationFailure({
			experienceTracker,
			experienceName: isInlineComment
				? DELETE_INLINE_COMMENT_EXPERIENCE
				: DELETE_PAGE_COMMENT_EXPERIENCE,
			error: err,
			commentId,
		});
	};

	const handleEditComment = () => {
		setCommentForEdit(commentId);
	};

	const exitCommentEditor = () => {
		setCommentForEdit('');
	};

	const handleReplyToComment = (id: string) => {
		setReplyToCommentId({
			parentCommentId: id,
			location: LocationValue.PANEL,
			user: fg('confluence_frontend_content_wrapper')
				? {
						displayName: comment?.author?.displayName,
						accountId: (comment?.author as InlineCommentAuthorKnownUser)?.accountId,
					}
				: undefined,
		});
	};

	const handleReopenMutationFailure = useCallback(
		({ isCacheUpdateFailed }: { isCacheUpdateFailed: boolean }) => {
			// TODO: Add experience tracking for reopening, which will allow us to differentiate and
			// detect cache errors vs mutation errors by the error value in the stopOnError() experience
			// call.  When that is added, we can go back to having a single flag message.
			void flags?.showErrorFlag({
				id: commentId,
				title: formatMessage(
					isCacheUpdateFailed
						? i18n.commentReopenCacheUpdateFailedFlagTitle
						: i18n.commentReopenFailedFlagTitle,
				),
				isAutoDismiss: true,
				actions: [
					{
						content: formatMessage(i18n.retryCTA),
						onClick: () => {
							void flags.hideFlag(flags.state.flags[0]?.id);
							handleReopenCommentRef.current?.();
						},
					},
				],
			});
		},
		[commentId, flags, formatMessage],
	);

	const reopenCommentUpdateFn: MutationUpdaterFn<CommentReopenCommentMutation_Mutation$data> =
		useCallback(
			(_dataProxy, mutationResult) =>
				updateReopenCommentInCache({
					mutationResult,
					commentId,
					updateCallback: reopenCommentInCacheCallback,
					onError: () => handleReopenMutationFailure({ isCacheUpdateFailed: true }),
				}),
			[commentId, handleReopenMutationFailure, updateReopenCommentInCache],
		);

	const handleReopenComment = () => {
		// NOTE: The reopen experience is started in CommentActions.tsx
		reopenCommentFn({
			variables: { commentId, cloudId },
			update: expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
				? reopenCommentUpdateFn
				: undefined,
		})
			.then(() => {
				// Update the list of active annotationIds
				addUnresolvedInlineComment(threadKey, pageMode);

				// Emit the event to editor/renderer to reactivate the annotationId
				if (!isDatabaseOrWhiteboard && commentType === CommentType.INLINE) {
					if (expVal('confluence_fe_resolve_reopen_comment_refactor', 'isEnabled', false)) {
						unresolveInlineCommentAnnotation(threadKey, pageMode, eventEmitter);
					} else {
						if (pageMode === PageMode.VIEW) {
							const emitter = getRendererAnnotationEventEmitter();
							emitter.emit(AnnotationUpdateEvent.SET_ANNOTATION_STATE, {
								[threadKey]: {
									id: threadKey,
									annotationType: AnnotationTypes.INLINE_COMMENT,
									state: AnnotationMarkStates.ACTIVE,
								},
							});
						} else {
							const emitter = getEditorAnnotationEventEmitter();
							emitter.emit('unresolve', threadKey);
						}
					}
				}

				// Update the state for panel experience (legacy only)
				if (!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
					if (expVal('confluence_fe_resolve_reopen_comment_refactor', 'isEnabled', false)) {
						reopenCommentInDataMap(
							commentsDataMap as LegacyCommentsDataMap,
							commentType,
							threadKey,
							addNewCommentThreads,
						);
					} else {
						const commentsDataMapClone = { ...commentsDataMap };
						const commentInMap = commentsDataMapClone[commentType][threadKey];
						if (commentInMap) {
							commentInMap.isOpen = true;
							commentInMap.wasRemovedByAnotherUser = false;

							const commentLocation = commentInMap.location as
								| CommentLocation
								| GeneralCommentLocation;

							if (
								commentLocation &&
								'commentResolveProperties' in commentLocation &&
								commentLocation.commentResolveProperties
							) {
								(commentLocation.commentResolveProperties as any).resolved = false;
							}

							if (
								commentLocation &&
								'inlineResolveProperties' in commentLocation &&
								commentLocation.inlineResolveProperties &&
								fg('confluence_frontend_content_wrapper')
							) {
								(commentLocation.inlineResolveProperties as any).resolved = false;
							}

							addNewCommentThreads(commentsDataMapClone as LegacyCommentsDataMap);
						}
					}
				}

				// Show the reopen flag
				void flags?.showSuccessCircleFlag({
					id: commentId,
					title: formatMessage(i18n.commentReopenedFlagTitle),
					isAutoDismiss: true,
					actions: [
						{
							content: formatMessage(commentsI18n.goToCommentCTA),
							onClick: () => {
								const isDangling = inlineCommentlocation?.inlineResolveProperties?.isDangling;
								let currentViewValue: ViewValues | undefined = undefined;

								if (expVal('confluence_comments_comments_6783', 'isEnabled', false)) {
									// COMMENTS-6783: Always call both `showCommentsPanel` and `setCurrentView`.
									// We can't reliably read `isCommentsPanelShown` here: this onClick is held by
									// the flags state container and the `Comment` component that registered it
									// is unmounted whenever the panel is closed, so any closure / ref over the
									// "panel shown" value is permanently stale by the time the user clicks
									// "Go to comment". `showCommentsPanel` is a no-op when the comments panel
									// is already shown (see `showObjectSidebar` in object-sidebar-api), and
									// `setCurrentView` is needed so that the view is updated when the panel
									// is already open. Together they handle both cases correctly.
									currentViewValue =
										isDangling && isNewDanglingCommentExperienceEnable
											? ViewValues.DELETED_CONTENT
											: ViewValues.OPEN;
									showCommentsPanel({ openWithView: currentViewValue });
									setCurrentView(currentViewValue);
								} else {
									// just in case the user closed the panel after flag is displayed but before the action is taken, we'll want to reopen the panel again
									// TOFIX: isCommentsPanelShown is still true after the panel is closed so we'll omit this check for now
									if (isDangling && isNewDanglingCommentExperienceEnable) {
										setCurrentView(ViewValues.DELETED_CONTENT);
										currentViewValue = ViewValues.DELETED_CONTENT;
									} else if (isCommentsPanelShown) {
										setCurrentView(ViewValues.OPEN);
										currentViewValue = ViewValues.OPEN;
									}
								}

								// Call navigation with view change waiting for reopen flow
								void handleCommentsPanelNavigationWithViewChange({
									scrollBehavior: ScrollBehavior.SMOOTH,
									context: NavigationContext.PANEL,
									block: 'center',
									annotationId: threadKey,
									commentType: commentType === CommentType.INLINE ? 'INLINE' : 'FOOTER',
									annotationClicked: true,
									expectedView: currentViewValue,
									commentId,
								});

								if (commentType === CommentType.INLINE || commentType === CommentType.GENERAL) {
									setCurrentlySelectedThreadKey(threadKey);
								}

								void flags.hideFlag(flags.state.flags[0]?.id);
							},
						},
						{
							content: formatMessage(commentsI18n.undoCTA),
							onClick: () => {
								handleResolveCommentRef.current?.();
								void flags.hideFlag(flags.state.flags[0]?.id);
							},
						},
					],
				});
				if (commentType === CommentType.INLINE) {
					if (isDatabaseOrWhiteboard && fg('cc-whiteboards-consolidating-comments')) {
						// add unresolved comment to whiteboard and database
						const commentInMap = commentsDataMap[commentType][threadKey];
						const commentLocation = commentInMap.location as CommentLocation;
						commentService.onReopenResolvedComment?.({
							contentId,
							referenceId: commentLocation.inlineMarkerRef!,
						});
					}

					const orderedActiveAnnotationIds = orderedActiveAnnotationIdList.map(
						(annotation) => annotation.threadKey,
					);

					// only insert reopened comment annotation if the comment highlight exists
					if (!(resolveProperties as CommentInlineResolveProperties)?.isDangling) {
						let annotationsList = annotations;
						if (pageMode === PageMode.VIEW) {
							const annotationMarks = rendererActions?.getAnnotationMarks();
							annotationsList = annotationMarks?.map((mark) => mark.attrs.id) ?? [];
						}

						if (annotationsList.length > 0) {
							insertReopenedCommentAnnotation(
								annotationsList,
								threadKey,
								orderedActiveAnnotationIds,
								setOrderedActiveAnnotationIdList,
							);
						}
					}
				}
			})
			.catch(() => {
				handleReopenMutationFailure({ isCacheUpdateFailed: false });
			});
	};

	const resolveExperienceName =
		commentType === CommentType.INLINE
			? RESOLVE_INLINE_COMMENT_EXPERIENCE
			: RESOLVE_PAGE_COMMENT_EXPERIENCE;

	const handleResolveMutationFailure = useCallback(
		(resolveError: Error) => {
			void handleMutationFailure({
				experienceTracker,
				experienceName: resolveExperienceName,
				error: resolveError,
			});
		},
		[experienceTracker, resolveExperienceName],
	);

	const resolveCommentsUpdateFn: MutationUpdaterFn<CommentResolveCommentMutation_Mutation$data> =
		useCallback(
			(_dataProxy, mutationResult) =>
				updateResolveCommentInCache({
					mutationResult,
					commentId,
					updateCallback: resolveCommentInCacheCallback,
					onError: handleResolveMutationFailure,
				}),
			[updateResolveCommentInCache, commentId, handleResolveMutationFailure],
		);

	let commentsDataManagerResolveUpdateFn = undefined;
	if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
		commentsDataManagerResolveUpdateFn = resolveCommentsUpdateFn; // FG enables new BE resolve comments endpoint
	}

	const handleResolveComment = () => {
		if (expVal('confluence_fe_resolve_comment_optimistic', 'isEnabled', false)) {
			// Optimistically mark the comment as resolved in the data map & update UI early (legacy only)
			if (!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
				resolveCommentInDataMap(
					commentsDataMap as LegacyCommentsDataMap,
					commentType,
					threadKey,
					addNewCommentThreads,
				);
			}
			// Optimistically mark the inline comment annotation as resolved
			if (!isDatabaseOrWhiteboard && commentType === CommentType.INLINE) {
				resolveInlineCommentAnnotation(threadKey, pageMode, eventEmitter);
			}
		}

		// NOTE: The RESOLVED experience is started in CommentActions.tsx
		resolveCommentFn({
			variables: { commentIds: [commentId], cloudId },
			update:
				(commentsDataManagerResolveUpdateFn as MutationUpdaterFn<CommentResolveCommentMutation_Mutation$data>) ||
				((cache, { data }) => {
					updateCommentsQueryCacheForResolve({
						cache,
						commentId,
						query: ActiveCommentsQuery,
						queryVariables: buildConsistentActiveCommentsQueryVariables({
							pageId: contentId,
							type: commentType === CommentType.GENERAL ? 'FOOTER' : undefined, // If we have footer, always write to footer. Panel works either way
						}),
						cacheUpdateFailed,
						setCacheUpdateFailed,
						source: AnalyticsSource.COMMENTS_PANEL,
						logger,
					});

					if (!isReply && (comment as CommentData).type === CommentType.GENERAL) {
						try {
							const { confluence_resolveComments } =
								data as CommentResolveCommentMutation_Mutation$data;

							if (!confluence_resolveComments) {
								return;
							}

							const { commentResolutionStates, success } = confluence_resolveComments;
							const commentsSectionQuery = isReactionsEnabled
								? CommentsSectionQuery
								: CommentsSectionWithoutReactionsQuery;

							if (
								!success ||
								commentResolutionStates === null ||
								commentResolutionStates.length === 0
							) {
								return;
							}

							const firstState = commentResolutionStates[0];
							if (!firstState) {
								return;
							}
							const { commentId } = firstState;

							const prevData = readQueryInterop<
								CommentsSectionQueryType | CommentsSectionWithoutReactionsQueryType,
								CommentsSectionQueryVariables
							>({
								query: commentsSectionQuery,
								variables: {
									contentId,
									offset: '',
									first: DEFAULT_LIMIT,
								},
							});

							const edges = prevData?.comments?.edges;

							// Filter out all comments related to the parent and it's children as they will all be resolved
							const newEdges =
								edges?.filter(
									(edge) =>
										edge?.node?.id !== commentId &&
										!edge?.node?.ancestors.map((a) => a?.id).includes(commentId),
								) || [];

							const newData = {
								comments: {
									...prevData?.comments,
									totalCount: newEdges.length,
									pageInfo: {
										...prevData?.comments?.pageInfo,
										endCursor: '',
										hasNextPage: false, //set hasNextPage to always false to avoid performing incremental fetching again which will result in duplicate data
									},
									edges: newEdges,
								},
								content: {
									...prevData?.content,
									nodes: [...(prevData?.content?.nodes || [])],
								},
								user: {
									...prevData?.user,
								},
							};

							writeQueryInterop({
								query: commentsSectionQuery,
								variables: {
									contentId,
									offset: '',
									first: DEFAULT_LIMIT,
								},
								data: {
									...newData,
								},
							});
						} catch (err) {
							logger.error`An error occurred when updating cache for general comments resolve - ${err}`;
							if (!expVal('confluence_fe_resolve_comment_optimistic', 'isEnabled', false)) {
								// CommentsSectionQuery is being deprecated. `readQuery` will throw error
								// if a matching cache entry is not present. This won't break any user experience.
								// However, setCacheUpdateFailed(true) will trigger a refetch of ActiveCommentsQuery,
								// whose result, with optimistic resolve, will include the newly resolved comment.
								// This causes comments panel to show the resolved comment. So skipping it here.
								setCacheUpdateFailed(true);
							}
						}
					}
				}),
			// When a `optimisticResponse` is set, useMutation invokes the update function twice:
			// first immediately, and after mutation succeeds, a second time. If mutation fails,
			// the cache updated by the update function is rolled back. Thus, this optimistic
			// resolve works both when cc_comments_new_comments_data_manager is ON and OFF.
			optimisticResponse: expVal('confluence_fe_resolve_comment_optimistic', 'isEnabled', false)
				? ({
						confluence_resolveComments: {
							commentResolutionStates: [
								{
									commentId,
									status: true,
									resolveProperties: {
										// These 2 values will be updated to the correct value after mutation succeeds.
										// - `resolvedUser` is only displayed in comments panel Resolved view,
										//   inside a tooltip when hovered.
										// - `resolvedFriendlyDate` is only used when confluence_frontend_content_wrapper
										//   is off; otherwise `resolvedTime` is used.
										// So setting them as empty optimistically won't affect the static UI.
										resolvedUser: null,
										resolvedFriendlyDate: '',
										resolvedTime: Date.now(),
										__typename: 'InlineCommentResolveProperties',
									},
									__typename: 'ConfluenceCommentResolutionState',
								},
							],
							success: true,
							errors: [],
							__typename: 'ConfluenceResolveCommentsPayload',
						},
						// typed as any because optimisticResponse is provided only for the resolve comment mutation
					} as CommentResolveCommentMutation_Mutation$data as any)
				: undefined,
		})
			.then(() => {
				if (!expVal('confluence_fe_resolve_comment_optimistic', 'isEnabled', false)) {
					// Update the state for panel experience when optimistic resolve is disabled
					// If enabled, this has been done before the mutation succeeds.
					if (!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
						if (expVal('confluence_fe_resolve_reopen_comment_refactor', 'isEnabled', false)) {
							resolveCommentInDataMap(
								commentsDataMap as LegacyCommentsDataMap,
								commentType,
								threadKey,
								addNewCommentThreads,
							);
						} else {
							const commentsDataMapClone = { ...commentsDataMap };
							const commentInMap = commentsDataMapClone[commentType][threadKey];

							if (commentInMap) {
								commentsDataMapClone[commentType][threadKey].isOpen = false;

								const commentLocation = commentsDataMapClone[commentType][threadKey].location as {
									commentResolveProperties?: {
										resolved: boolean;
									};
									inlineResolveProperties?: {
										resolved: boolean;
										isDangling?: boolean;
									};
								};
								if (commentLocation && commentLocation.inlineResolveProperties) {
									commentLocation.inlineResolveProperties.resolved = true;
								}

								if (commentLocation && commentLocation.commentResolveProperties) {
									commentLocation.commentResolveProperties.resolved = true;
								}

								addNewCommentThreads(commentsDataMapClone as LegacyCommentsDataMap);
							}
						}
					}
				}

				// Update content types comment service if we are resolving a comment
				commentService.onResolveComment?.(threadKey);

				if (
					commentType === CommentType.INLINE ||
					!expVal('confluence_fe_resolve_comment_optimistic', 'isEnabled', false)
				) {
					// (Putting this behind the experiment just in case.)
					// This handles INLINE comment annotation. No need to invoke it for GENERAL comment.
					handleResolveSuccess({
						commentId,
						parentCommentMarkerRef: threadKey,
						pageId: contentId,
						pageMode,
						commentType,
						eventEmitter,
						source: AnalyticsSource.COMMENTS_PANEL,
						removeUnresolvedInlineComment,
						createAnalyticsEvent,
						setEditorViewProps,
						//getInlineNodeTypes  // TODO: Figure out how to get this info
					});
				}

				setOrderedActiveAnnotationIdList(
					orderedActiveAnnotationIdList
						.map((status) => status.threadKey)
						.filter((id) => id !== threadKey),
				);

				// Show the resolved flag
				void flags?.showSuccessCircleFlag({
					id: commentId,
					title: formatMessage(commentsI18n.resolvedFlagText),
					isAutoDismiss: true,
					actions: [
						{
							content: formatMessage(commentsI18n.goToCommentCTA),
							onClick: () => {
								if (expVal('confluence_comments_comments_6783', 'isEnabled', false)) {
									// COMMENTS-6783: Always call both `showCommentsPanel` and
									// `setCurrentView`. We can't reliably read `isCommentsPanelShown`
									// here: this onClick is held by the flags state container, and the
									// `Comment` component that registered it is unmounted whenever the
									// panel is closed, so any closure / ref over the "panel shown" value
									// is permanently stale by the time the user clicks "Go to comment".
									// `showCommentsPanel` is a no-op when the comments panel is already
									// shown (see `showObjectSidebar` in object-sidebar-api), and
									// `setCurrentView` is needed so the view is updated when the panel
									// is already open. Together they handle both cases correctly.
									showCommentsPanel({ openWithView: ViewValues.RESOLVED });
									setCurrentView(ViewValues.RESOLVED);
								} else if (!isCommentsPanelShown) {
									showCommentsPanel({ openWithView: ViewValues.RESOLVED });
								} else {
									setCurrentView(ViewValues.RESOLVED);
								}

								void handleCommentsPanelNavigationWithViewChange({
									scrollBehavior: ScrollBehavior.SMOOTH,
									context: NavigationContext.PANEL,
									block: 'center',
									annotationId: threadKey,
									commentType: commentType === CommentType.INLINE ? 'INLINE' : 'FOOTER',
									annotationClicked: true,
									expectedView: ViewValues.RESOLVED,
									commentId,
								});

								void flags.hideFlag(flags.state.flags[0]?.id);
							},
						},
						{
							content: formatMessage(commentsI18n.undoCTA),
							onClick: () => {
								if (expVal('confluence_comments_comments_6783', 'isEnabled', false)) {
									// COMMENTS-6783: Always re-open the panel to the OPEN view.
									// `showCommentsPanel` is a no-op when the panel is already shown
									// (see `showObjectSidebar` in object-sidebar-api). We can't reliably
									// read `isCommentsPanelShown` here because the `Comment` component
									// that registered this onClick is unmounted whenever the panel is
									// closed, so any closure / ref over the "panel shown" value is
									// permanently stale.
									const isDangling = inlineCommentlocation?.inlineResolveProperties?.isDangling;
									const currentViewValue =
										isDangling && isNewDanglingCommentExperienceEnable
											? ViewValues.DELETED_CONTENT
											: ViewValues.OPEN;
									showCommentsPanel({ openWithView: currentViewValue });
								} else if (!isCommentsPanelShown) {
									showCommentsPanel({ openWithView: ViewValues.OPEN });
								}

								handleReopenComment();
								void flags.hideFlag(flags.state.flags[0]?.id);
							},
						},
					],
				});

				experienceTracker.succeed({
					name: resolveExperienceName,
				});
			})
			.catch((err) => {
				if (expVal('confluence_fe_resolve_comment_optimistic', 'isEnabled', false)) {
					// Undo resolving the comment when optimistic resolve is enabled (legacy only)
					if (!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
						reopenCommentInDataMap(
							commentsDataMap as LegacyCommentsDataMap,
							commentType,
							threadKey,
							addNewCommentThreads,
						);
					}
					// Undo resolving the inline comment annotation
					if (!isDatabaseOrWhiteboard && commentType === CommentType.INLINE) {
						unresolveInlineCommentAnnotation(threadKey, pageMode, eventEmitter);
					}

					// Show an error flag to user when resolve fails
					void flags?.showErrorFlag({
						id: commentId,
						title: formatMessage(commentsI18n.resolveErrorTitle),
						description: formatMessage(commentsI18n.resolveError),
					});
				}

				handleResolveMutationFailure(err);
			});
	};

	// Update refs to break circular dependency
	handleResolveCommentRef.current = handleResolveComment;
	handleReopenCommentRef.current = handleReopenComment;

	const pageType = pageInfo?.type ?? '';

	// Editor setup
	const spaceId = pageInfo?.space?.id ?? '';

	// User page permissions
	const operations = pageInfo?.operations || [];

	// User space permissions - only access when feature gate is on
	const spaceOperations = fg('confluence_comments_space_operations_permissions')
		? pageInfo?.space?.operations || []
		: [];

	const canAddComment = operations.some(
		(op) => op?.operation === 'create' && op?.targetType === 'comment',
	);

	const canAddAttachment =
		operations.some((op) => op?.operation === 'create' && op?.targetType === 'attachment') ||
		spaceOperations.some((op) => op?.operation === 'create' && op?.targetType === 'attachment');

	// The user can upload media only if they have update permissions for the page if confluence_comment_use_comment_media_endpoint FG is off
	const hasMediaUploadPermissions = fg('confluence_comment_use_comment_media_endpoint')
		? canAddComment && canAddAttachment
		: operations.some((op) => op?.operation === 'update' && op?.targetType === pageType);

	// We only want to focus resolved comments because the 'active' comments have
	// their own treatment for when one is hovered/selected and could cause weird behavior
	const isCommentFocused = resolveProperties?.resolved && commentId === focusedCommentId;

	const supportedActions =
		!isReply && isCommentRemovedByAnotherUser
			? (['reopen'] as CommentAction[])
			: supportedTopLevelActions;

	const shouldShowLeftBorder = expValEquals('confluence_comments_tuneup', 'isEnabled', true)
		? commentHandlesPadding && commentId === editCommentId && isReply && !isLastUnnestableReply
		: commentHandlesPadding && !isStandaloneComment && commentId === editCommentId && isReply;

	const handleStandaloneReply = (id: string) => {
		replyToComment && replyToComment(id, comment);
	};

	return (
		<div
			css={[
				commentHandlesPadding && commentWrapperReplyPaddingOverrides,
				commentHandlesPadding &&
					expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
					commentWrapperReplyPaddingOverridesV2,
				commentHandlesPadding &&
					isStandaloneComment &&
					expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
					standaloneCommentWrapperReplyPaddingOverrides,
				shouldShowLeftBorder && leftBorderStyle,
				shouldShowLeftBorder && fg('confluence_comment_border_migration') && leftBorderStyleUpdated,
			]}
		>
			{commentId === editCommentId ? (
				<EditComment
					pageId={contentId}
					pageType={pageType}
					annotationId={threadKey}
					commentId={commentId}
					spaceId={spaceId}
					content={content ?? ''}
					isReply={isReply}
					displayCommentInViewMode={exitCommentEditor}
					avatarUrl={topCommentUserAvatar ?? ''}
					displayName={topCommentDisplayName}
					date={date ?? ''}
					dateUrl={commentUrl ?? ''}
					createdAtNonLocalized={createdAtNonLocalized ?? ''}
					versionByDisplayName={versionByDisplayName}
					versionDate={versionDate ?? ''}
					resolveProperties={resolveProperties}
					mode={pageMode === PageMode.VIEW ? 'view' : 'edit'}
					hasMediaUploadPermissions={hasMediaUploadPermissions}
					isCommentsPanel={isCommentsPanelShown}
					commentType={commentType}
					isStandaloneComment={isStandaloneComment}
					isOffline={isOffline}
					isLastUnnestableReply={isLastUnnestableReply}
					hasParentMaxHeight={hasParentMaxHeight}
					numReplies={numReplies}
				/>
			) : (
				<CommentBody
					mode="view"
					userId={userId}
					avatarUrl={topCommentUserAvatar}
					displayName={topCommentDisplayName}
					date={date ?? ''}
					dateUrl={commentUrl ?? ''}
					createdAt={createdAt ?? ''}
					createdAtNonLocalized={createdAtNonLocalized ?? ''}
					versionByDisplayName={versionByDisplayName}
					versionDate={versionDate ?? ''}
					pageId={contentId}
					pageType={pageType}
					commentId={commentId ?? ''}
					isReply={isReply}
					isEdited={isEdited}
					parentCommentId={parentCommentId}
					permissions={permissions}
					content={content ?? ''}
					permissionType={(permissionType as any) ?? undefined}
					supportedActions={supportedActions}
					deleteComment={
						commentType === CommentType.INLINE
							? handleDeleteInlineComment
							: handleDeleteGeneralComment
					}
					editComment={handleEditComment}
					resolveComment={handleResolveComment}
					numReplies={numReplies}
					isUnread={isUnread}
					isCommentActive
					hideAiSuggestEdit={
						currentView === ViewValues.RESOLVED || currentView === ViewValues.DELETED_CONTENT
					}
					isCommentsPanel
					isHovered={isHovered}
					isResolvedByAnotherUser={isResolvedByAnotherUser}
					isFocused={isCommentFocused}
					isOffline={isOffline}
					resolveProperties={resolveProperties}
					annotationId={threadKey}
					annotatedText={annotatedText}
					reopenComment={handleReopenComment}
					replyToComment={isStandaloneComment ? handleStandaloneReply : handleReplyToComment}
					inheritedReactionsData={inheritedReactionsData}
					isDeletedByAnotherUser={isDeletedByAnotherUser}
					currentlySelectedThreadKey={currentlySelectedThreadKey}
					setCurrentlySelectedThreadKey={setCurrentlySelectedThreadKey}
					isAnnotatedTextPressed={isAnnotatedTextPressed}
					setAnnotatedTextPressed={setAnnotatedTextPressed}
					commentType={commentType}
					adjustForNestedReplies={adjustForNestedReplies}
					hideBranchingStyle={hideBranchingStyle}
					canAddComments={canAddComments}
					isThreadHovered={isThreadHovered}
					isParentCommentOpen={isParentCommentOpen}
					reactionsCacheUpdateFn={updateReactionsCacheFn}
					isStandaloneComment={isStandaloneComment}
					showRepliesWithCurvedBorders={showRepliesWithCurvedBorders}
					onRendered={onRendered}
				/>
			)}
		</div>
	);
};

// Wrap with React.memo gated by the comments-panel-memo-rows experiment.
// When the experiment is enabled, shallow-equal props will skip a re-render
// of the heavy Comment subtree (CommentBody, EditComment, mutations) during
// unrelated parent re-renders. When disabled, the equality function returns
// false and behaviour matches the pre-experiment implementation.
export const Comment: typeof CommentComponent = memo(
	CommentComponent,
	arePropsShallowEqualWhenMemoRowsEnabled,
) as typeof CommentComponent;
